import React, { useState, useMemo, useEffect } from 'react';
import { HUG_FULL_BOOKING, HUG_DAY_BOOKING, HUG_PARTIAL_BOOKING, HUG_ONETIME_BOOKING} from '../services/ConstHelper';
import {Roles} from '../Configs/DatabaseConsts';
import { MembershipTypes } from '../Configs/DatabaseConsts';
import { fetchApi } from '../services/HTTP';
import { useToast } from 'react-native-toast-notifications';
import { t } from '../services/i18n';
import { flowOptions } from '../components/Structure/Schedule/Popups/CreateEvent/FlowOptions';
import { openStepperModal } from '../services/stepperService';
import {
    closeModal,
    CREATE_EVENT_MODAL,
    EVENT_MANAGEMENT_MODAL,
    hideModal, openModal, setLoadingModal,
    STEPPER_CONFIRM_MODAL,
    updateModalProps
} from '../redux/modalManagerSlice';
import { cleanStepper } from '../redux/stepperSlice';
import {useDispatch, useSelector} from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {dbTimeFormat, getBookingOptionLabel, isMobile, getMessageToUserFromError, isScheduleException, sendDataEvent} from "../services/Helpers";
import moment from "moment/moment";
import AppText from "../components/common/AppText";
import _ from "lodash";
import {Stack, VStack} from "native-base";
import {Radio} from "antd";
import HugBookingOptionsCheckboxes
    from "../components/Structure/Schedule/Popups/EventManage/HugBookingOptionsCheckboxes";
import usePermissions from '../services/Permissions';
import { bookingWithoutMembership } from '../services/Helpers';

export const CONFIRM_TYPES = {
    BOOK_TO_HUG: 'bookToHug',
    BOOK_LEAD_TO_HUG: 'bookLeadToHug',
    BOOK_NEW_LEAD_TO_TRIAL: 'bookNewLeadToTrial',
};

export const SELECT_NEW_LEAD = 'select_new_lead';
export const SELECT_NEW_MEMBER = 'select_new_member';

const useBookingOptions = ({ selectedUser, selectedBookingType, eventData, fetchScheduleData, setSelectedUser}) => {
    const [bookingType, setBookingType] = useState(selectedBookingType || HUG_FULL_BOOKING);
    const [seriesEvents, setSeriesEvents] = useState(null);
    const [selectedNumOfDays, setSelectedNumOfDays] = useState(null);
    const [showError, setShowError] = useState(false);
    const [confirmType, setConfirmType] = useState(null);
    const [isTrialBooking, setIsTrialBooking] = useState(false);

    const lang = useSelector(state => state.userSettings.lang);
    const box = useSelector(state => state.box.box)
    const selectedLocation = useSelector((state) => state.userSettings.scheduleLocation);
    const isStepperConfirmModalOpen = useSelector((state) => state.modalManager.stepperConfirmModal.isOpen);

    const displayPartialOptions = useMemo(() => eventData.series && eventData.series.partialBookingOptions?.length > 0 ? Object.values(_.groupBy(eventData.series.partialBookingOptions, 'numOfDays')).map(group => group[0]) : null, [eventData]);
    const displayOnetimeOption = useMemo(() => eventData.series && eventData.series.membership_types_all?.find(mt => mt.type === MembershipTypes.ONETIME), [eventData]);
    const boxLocale = useMemo(() => lang?.language === 'Hebrew' ? 'he' : 'en', [lang])
    const timeFormat = useMemo(() => box ? box.locations_box[0].time_format : null,[box])

    const isValid = useMemo(() =>
        [HUG_FULL_BOOKING, HUG_DAY_BOOKING, HUG_ONETIME_BOOKING].includes(bookingType) || seriesEvents?.length === selectedNumOfDays,
        [seriesEvents, selectedNumOfDays, bookingType]
    );

    const toast = useToast();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { has } = usePermissions()

    useEffect(() => {
        if (!selectedUser) return;
        if (selectedUser === SELECT_NEW_LEAD) {
            handleAddNewLead();
        } else {
            handleBookUserToHug(selectedUser);
        }
    }, [selectedUser])

    useEffect(() => {
        if(bookingType) {
            handleBookingOptionChange(bookingType)
        }
    }, [bookingType])

    useEffect(() => {
        if (!isStepperConfirmModalOpen) return;
        if (!confirmType) return;
        dispatch(updateModalProps({
            modalName: STEPPER_CONFIRM_MODAL,
            props: getConfirmProps(confirmType)
        }));
    }, [bookingType, seriesEvents, showError])

    const handleBookUserToHug = (user, force = null, isTrial = false) => {
        if(!force) {
            checkUserAlreadyBookedHug(user);
        }
        else {
            dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}));
            openBookToHugFlow((isTrialBooking || isTrial) ? flowOptions.BOOK_TO_HUG_TRIAL : null)
        }
    }

    const openBookToHugFlow = (initSelection = null) => {
        dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}));
        openStepperModal({ modalName: CREATE_EVENT_MODAL, dispatch,
            props: {
                initSelection: initSelection ?? flowOptions.BOOK_TO_HUG_FLOW,
                target: {eventData, user: {...selectedUser, firstName: selectedUser?.first_name, lastName: selectedUser?.last_name}, bookingType, selectedSeriesEvents: seriesEvents},
                user: selectedUser === SELECT_NEW_MEMBER ? null : selectedUser,
                navigate
            },
            onClose: () => {
                resetBookingOptions();
                dispatch(cleanStepper());
                dispatch(hideModal({ modalName: EVENT_MANAGEMENT_MODAL, hide: false }));
            }
        });
        dispatch(hideModal({ modalName: EVENT_MANAGEMENT_MODAL, hide: true }));
    }

    const checkUserAlreadyBookedHug = async (user) => {
        try {
            const isLead = user?.type === Roles.LEAD.toLowerCase()
            const trialMT = eventData.series.membership_types_all.find(mt => mt.type === MembershipTypes.TRIAL_CLASS)
            if(user) {
                await fetchApi('hugim/checkIfUserAlreadyBookedToHug', 'post', {series_id: eventData.series.id, user_fk: user.user_fk, schedule_fk: eventData.id, isTrial: !!(isLead && trialMT)}, false, true);
            }
            if(isLead) {
                if(trialMT) {
                    setBookingType(HUG_DAY_BOOKING);
                    setConfirmType(CONFIRM_TYPES.BOOK_LEAD_TO_HUG);
                    openChooseBookingTypeModal(CONFIRM_TYPES.BOOK_LEAD_TO_HUG);
                } else {
                    toast.show(t('hugHasNoTrialMembership'),{type: "danger", duration: 2000});
                }
            } else {
                setConfirmType(CONFIRM_TYPES.BOOK_TO_HUG);
                openChooseBookingTypeModal(CONFIRM_TYPES.BOOK_TO_HUG);
            }
        } catch (e) {
            if(e.error?.message === 'userAlreadyBookedToHug') {
                toast.show(e.error.messageToUser,{
                    type: "danger",
                    duration: 2000,
                });
            }
        }
    }

    const handleHugPopupSelection = async () => {
        if (!validate()) return null;
        if(bookingType !== HUG_DAY_BOOKING && bookingType !== HUG_ONETIME_BOOKING) {
            if(bookingType === HUG_FULL_BOOKING) {
                try{
                    await fetchApi('hugim/checkBookingRestrictions', 'post', {series_id: eventData.series.id, user_fk: selectedUser?.user_fk}, false, true);
                    openBookToHugFlow();
                }
                catch (e) {
                    console.log('e', e)
                    dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}));
                    openErrorConfirmModal(`${e.error?.messageToUser} \n${t('book-anyway-question')}`, () => handleBookUserToHug(selectedUser, true));
                }
            } else {
                openBookToHugFlow();
            }
        }
        else {
            const isRestricted = checkUserIsRestricted();
            if(!isRestricted) {
                if(bookingType === HUG_ONETIME_BOOKING) {
                    openBookToHugFlow(flowOptions.BOOK_TO_HUG_ONETIME)
                } else {
                    await handleOneTimeBookingWithoutMembership(selectedUser, null, false);                }
            }
        }
    }

    const handleOneTimeBookingWithoutMembership = async (data, force = null) => {
        try {
            await bookingWithoutMembership(data, null, force, null, true, null, eventData, 'scheduleUser');
            toast.show(t('book-without-membership-success'),{type: "success", duration: 2000});
            fetchScheduleData(true);
        } catch (e) {
            console.log('e.error', e)
            const errorText = getMessageToUserFromError(e.error);
            if (isScheduleException(e.error)) {
                openErrorConfirmModal(`${errorText} \n\n${t('book-anyway-question')}`, () => handleOneTimeBookingWithoutMembership(data, true));
            } else {
                toast.show(t(errorText),{
                    type: "danger",
                    duration: 2000,
                });
            }
        }
    }


    const handleBookLeadToHugPopupSelection = () => {
        if(bookingType !== HUG_DAY_BOOKING) {
            handleConvertToMember()
        } else if(bookingType === HUG_DAY_BOOKING) {
            handleLeadTrialToHug()
        }
    }

    const handleConvertToMember = () => {
        if (!validate()) return null;
        openBookToHugFlow(flowOptions.BOOK_TO_HUG_CONVERT_LEAD)
    }


    const handleLeadTrialToHug = async () => {
        try {
            setIsTrialBooking(true)
            await fetchApi('schedule/isUserValidForTrial', 'post', {schedule_id: eventData.id, user_id: selectedUser?.user_fk, flowName: flowOptions.BOOK_TO_HUG_TRIAL}, false, true);
            openBookToHugFlow(flowOptions.BOOK_TO_HUG_TRIAL)
        } catch(e) {
            const errorText = getMessageToUserFromError(e.error);
            openErrorConfirmModal(`${errorText} \n${t('book-anyway-question')}`, () => handleBookUserToHug(selectedUser, true, true));
        }
    }

    const handleBookNewLeadToTrialConfirm = () => {
        dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}));
        openStepperModal({
            modalName: CREATE_EVENT_MODAL,
            dispatch: dispatch,
            props: { initSelection: flowOptions.BOOK_TO_HUG_CREATE_LEAD, fromEventManage: true, target: {eventData, selectedNumOfDays, seriesEvents}, navigate,},
            onClose: () => {
                resetBookingOptions();
                dispatch(cleanStepper());
                dispatch(hideModal({ modalName: EVENT_MANAGEMENT_MODAL, hide: false }))
            }
        });
        dispatch(hideModal({ modalName: EVENT_MANAGEMENT_MODAL, hide: true }))
    }

    const handleAddNewLead = async () => {
        const trialMT = eventData.series.membership_types_all.find(mt => mt.type === MembershipTypes.TRIAL_CLASS);
        if (trialMT) {
            try {
                await fetchApi('hugim/checkBookingRestrictions', 'post', {series_id: eventData.series.id, user_fk: selectedUser?.user_fk}, false, true);
                setConfirmType(CONFIRM_TYPES.BOOK_NEW_LEAD_TO_TRIAL);
                openChooseBookingTypeModal(CONFIRM_TYPES.BOOK_NEW_LEAD_TO_TRIAL);
            } catch (e) {
                console.log('e', e)
                openErrorConfirmModal(`${e.error?.messageToUser} \n${t('book-anyway-question')}`, () => {
                    setConfirmType(CONFIRM_TYPES.BOOK_NEW_LEAD_TO_TRIAL);
                    setTimeout(() => openChooseBookingTypeModal(CONFIRM_TYPES.BOOK_NEW_LEAD_TO_TRIAL), 150);
                });
            }
        } else {
            toast.show(t('hugHasNoTrialMembership'), {type: "danger", duration: 2000});
        }
    };

    const checkUserIsRestricted = () => {
        if(selectedUser?.restricted) {
            openErrorConfirmModal(t('bookToHugRestrictedUserError'));
            return true;
        }
    }

    const handleBookingOptionChange = (value) => {
        setShowError(false);
        setBookingType(value);
        setSeriesEvents(null);
        if (value.startsWith(HUG_PARTIAL_BOOKING)) {
            const parts = value.split('_');
            setSelectedNumOfDays(parseInt(parts[parts.length - 1]));
        } else {
            setSelectedNumOfDays(null);
        }
    };

    const formatAndSetConfirmationText = () => {
        const formattedDateSingle = `${moment(eventData.date).locale(boxLocale).format(boxLocale === 'he' ? 'D MMM, YYYY' : "MMM D, YYYY")}`
        return <>
            <div>{(displayPartialOptions || selectedUser?.full_name) && bookOptionsRadioJSX}</div>
            <AppText mediumFont>{t('bookingDetails')}:</AppText>
            <div>{getPeriodConfirmText()}</div>
            {(bookingType === HUG_DAY_BOOKING || bookingType === HUG_ONETIME_BOOKING) && <div>{formattedDateSingle} {t('at')} {eventData.time} - {eventData.end_time}</div>}
            {bookingType !== HUG_DAY_BOOKING && bookingType !== HUG_ONETIME_BOOKING && getFormatConfirmationText()}
        </>;
    }

    const getFormatConfirmationText = () => {
        let daysAndHours
        const allSeriesEvents = eventData.series.series_events.map(seriesEvent => ({id: seriesEvent.id, daysAndHours: `${moment().weekday(seriesEvent.day_of_week).locale(boxLocale).format("dddd")}: ${moment(seriesEvent.start, dbTimeFormat).locale(boxLocale).format(timeFormat)} - ${moment(seriesEvent.end, dbTimeFormat).locale(boxLocale).format(timeFormat)}${seriesEvent.description ? ` - ${seriesEvent.description}` : ''}`}))
        if(bookingType !== HUG_FULL_BOOKING) {
            daysAndHours = allSeriesEvents.filter(seriesEvent => seriesEvents?.includes(seriesEvent.id)).map(seriesEvent => seriesEvent.daysAndHours).join('\n')
        } else {
            daysAndHours = allSeriesEvents.map(seriesEvent => seriesEvent.daysAndHours).join('\n')

        }
        const formattedDate = `${moment(eventData.date).locale(boxLocale).format(boxLocale === 'he' ? 'D MMM, YYYY' : "MMM D, YYYY")} - ${eventData.series?.end_date ? moment(eventData.series?.end_date).locale(boxLocale).format(boxLocale === 'he' ? 'D MMM, YYYY' : "MMM D, YYYY") : t('no-end-date-hugim', {startDate: moment(eventData.date).locale(boxLocale).format(boxLocale === 'he' ? 'D MMM, YYYY' : "MMM D, YYYY")})}`
        return <>
            <div>{formattedDate}</div>
            <div>{daysAndHours}</div>
        </>
    }

    const getPeriodConfirmText = () => {
        if(bookingType === HUG_FULL_BOOKING) {
            if(eventData.series?.start_date === eventData.date) {
                return `${t(selectedUser?.full_name ? 'followingPeriod' : 'followingPeriodNewMember', {userName: (selectedUser?.full_name)})}`;
            } else {
                return `${t('hugMidBookingConfirmExplain')}\n${t(selectedUser?.full_name ? 'followingPeriod' : 'followingPeriodNewMember', {userName: selectedUser?.full_name})}`;
            }
        }
        else if(bookingType === HUG_DAY_BOOKING) {
            return t('oneTimeHugExplain', {userName: selectedUser?.full_name});
        }
        else if(bookingType === HUG_ONETIME_BOOKING) {
            return t('onetime-booking-explain',{userName: selectedUser?.full_name})
        }
        else {
            if(eventData.series?.start_date === eventData.date) {
                return `${t(selectedUser?.full_name ? 'followingPeriodPartial' : 'followingPeriodNewMemberPartial', {userName: (selectedUser?.full_name)})}`;
            } else {
                return `${t('hugMidBookingConfirmExplain')}\n${t(selectedUser?.full_name ? 'followingPeriodPartial' : 'followingPeriodNewMemberPartial', {userName: selectedUser?.full_name})}`;
            }
        }
    }

    const getLeadTrialHugSubheader = () => {
        const trialMT = eventData.series.membership_types_all.find(mt => mt.type === MembershipTypes.TRIAL_CLASS)
        const isFree = trialMT.price === 0
        const params = {
            userName: selectedUser?.full_name,
            price: trialMT.price > 0 ? `${selectedLocation.currency_symbol}${trialMT.price}` : t('free'),
            date: moment(eventData.date).locale(boxLocale).format(boxLocale === 'he' ? 'Do MMM' : 'MMM Do'),
            time: `${eventData.time} - ${eventData.end_time}`,
            day: moment().weekday(eventData.day_of_week).locale(boxLocale).format("dddd")
        }
        return <>
            <VStack>
                <Stack space={'0.5rem'} mb={'15px'}>
                    <AppText mediumFont>{t('bookingLeadOptions')}</AppText>
                    <Radio.Group onChange={(e) => setBookingType(e.target.value)} value={bookingType}>
                        <VStack space={'0.5rem'}>
                            <Radio value={HUG_DAY_BOOKING} size={'sm'} checked>{t('bookTriaClassOption')}</Radio>
                            <Radio value={HUG_FULL_BOOKING} size={'sm'}>{t('convertToMemberOption')}</Radio>
                            {has('managePartialBooking') && displayPartialOptions && displayPartialOptions.map(option =>
                                <Radio key={option.numOfDays} value={HUG_PARTIAL_BOOKING + `_${option.numOfDays}`} size={'sm'} disabled={!option.canBook} checked>
                                    {getBookingOptionLabel(option.numOfDays)}
                                </Radio>)}
                        </VStack>
                    </Radio.Group>
            <HugBookingOptionsCheckboxes eventData={eventData} bookingType={bookingType} showError={showError} onSelectSeriesEvents={setSeriesEvents} selectedSeriesEvents={seriesEvents} />
        </Stack>
        <AppText mediumFont>{t('bookingLeadDetails')}</AppText>
                {bookingType === HUG_DAY_BOOKING && <div>{t(`leadTrialConfirmSubheader${isFree ? '-free' : ''}`, params)}</div>}
                {bookingType !== HUG_DAY_BOOKING && <div>{t('leadConvertConfirmSubheader', {userName: params?.userName})}</div>}
                {bookingType !== HUG_DAY_BOOKING && getFormatConfirmationText()}
            </VStack>
        </>;
    }

    const getNewLeadTrialConfirmSubheader = () => {
		const trialMT = eventData.series.membership_types_all.find(mt => mt.type === MembershipTypes.TRIAL_CLASS)
		const isFree = trialMT?.price === 0
		const params = {
			price: trialMT?.price > 0 ? `${selectedLocation.currency_symbol}${trialMT?.price}` : t('free'),
			date: moment(eventData.date).locale(boxLocale).format(boxLocale === 'he' ? 'Do MMM' : 'MMM Do'),
			time: `${eventData.time} - ${eventData.end_time}`,
            day: moment().weekday(eventData.day_of_week).locale(boxLocale).format("dddd")
        }
		return t(isFree ? 'newLeadTrialConfirmSubheaderFree' : 'newLeadTrialConfirmSubheaderPaid', params)
	}


    const bookOptionsRadioJSX =
        <Stack space={'0.5rem'} mb={'15px'}>
            <AppText mediumFont>{t('bookingOptions')}:</AppText>
            <Radio.Group onChange={(e) => setBookingType(e.target.value)} value={bookingType}>
                <VStack space={'0.5rem'}>
                    <Radio value={HUG_FULL_BOOKING} size={'sm'} checked>{getBookingOptionLabel(null)}</Radio>
                    {has('managePartialBooking') && displayPartialOptions && displayPartialOptions.map(option =>
                        <Radio key={option.numOfDays} value={HUG_PARTIAL_BOOKING + `_${option.numOfDays}`} size={'sm'} disabled={!option.canBook} checked>
                            {getBookingOptionLabel(option.numOfDays)}
                        </Radio>)}
                    {selectedUser?.full_name && <Radio value={HUG_DAY_BOOKING} size={'sm'}>{t('bookOneTimeHugOption')}</Radio>}
                    {selectedUser?.full_name && displayOnetimeOption && <Radio value={HUG_ONETIME_BOOKING} size={'sm'}>{displayOnetimeOption.price > 0 ? t('bookOneTimeHugOptionPaid', {price: `${selectedLocation.currency_symbol}${displayOnetimeOption.price}`}) : t('bookOneTimeHugOptionFree')}</Radio>}

                </VStack>
            </Radio.Group>
            <HugBookingOptionsCheckboxes eventData={eventData} bookingType={bookingType} showError={showError} onSelectSeriesEvents={setSeriesEvents} selectedSeriesEvents={seriesEvents} />
        </Stack>;

    const validate = () => {
        if (!isValid) setShowError(true);
        return isValid;
    };

    const getConfirmProps = (type) => {
        switch (type) {
            case CONFIRM_TYPES.BOOK_TO_HUG:
                return {
                    header: t('bookToHug'),
                    additions: formatAndSetConfirmationText(),
                    beforeConfirm: validate,
                    onConfirm: () => {
                        dispatch(setLoadingModal({modalName: STEPPER_CONFIRM_MODAL, isConfirmLoading: true}))
                        handleHugPopupSelection()
                    },
                    confirmText: bookingType === HUG_FULL_BOOKING ? t('confirmBookingHug') : t('confirm'),
                };
            case CONFIRM_TYPES.BOOK_LEAD_TO_HUG:
                return {
                    header: t('bookLeadToHug'),
                    additions: getLeadTrialHugSubheader(),
                    beforeConfirm: validate,
                    onConfirm: () => {
                        dispatch(setLoadingModal({modalName: STEPPER_CONFIRM_MODAL, isConfirmLoading: true}))
                        handleBookLeadToHugPopupSelection()
                    },
                    confirmText: bookingType === HUG_FULL_BOOKING ? t('BookToHugConvertLead') : t('BookToHugTrialLead')
                };
            case CONFIRM_TYPES.BOOK_NEW_LEAD_TO_TRIAL:
                return {
                    header: t('newLeadTrialConfirmHeader'),
                    additions: getNewLeadTrialConfirmSubheader(),
                    beforeConfirm: validate,
                    onConfirm: () => {
                        dispatch(setLoadingModal({modalName: STEPPER_CONFIRM_MODAL, isConfirmLoading: true}))
                        handleBookNewLeadToTrialConfirm()
                    },
                    confirmText: t('BookToHugTrialLead')
                };
            default:
                return {};
        }
    };

    const resetBookingOptions = () => {
        setSelectedUser(null);
        setBookingType(HUG_FULL_BOOKING);
        setSeriesEvents(null);
        setSelectedNumOfDays(null);
        setShowError(false);
    }

    const openChooseBookingTypeModal = (type) => {
        dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}))
        dispatch(openModal({modalName: STEPPER_CONFIRM_MODAL, props: {
            isNotError: true,
            containerDOM: isMobile && '.event-click-popup',
            onClose: () => {
                resetBookingOptions()
                dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}))
            },
            onCancel: () => {
                resetBookingOptions()
                dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}))
            }
            ,
            ...getConfirmProps(type)
        }}))
    };

    const openErrorConfirmModal = (errorConfirm, onConfirm) => {
        setConfirmType(null);
        dispatch(openModal({modalName: STEPPER_CONFIRM_MODAL, props: {
            isNotError: false,
            header: t('areYouSure'),
            subheader: errorConfirm,
            onConfirm: () =>{
                dispatch(setLoadingModal({modalName: STEPPER_CONFIRM_MODAL, isConfirmLoading: true}))
                onConfirm()
            },
            onClose: () => {
                resetBookingOptions()
                dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}))
            },
            onCancel: () => {
                resetBookingOptions()
                dispatch(closeModal({ modalName: STEPPER_CONFIRM_MODAL}))
            },
            containerDOM: isMobile && '.event-click-popup',
        }}))
    }


    return {
        seriesEvents,
        selectedNumOfDays,
    };
};

export default useBookingOptions;
